import type { ExpansionId, Rotation, WallSide, ZoneId } from '@/core/content/ids';
import type { ItemDef } from '@/core/content/schemas/items';
import type { GridRect, RoomLayout } from '@/core/content/schemas/rooms';

/** Grid geometry for placement rules and right-spot checks (content-schemas §3.6, §14.2). Pure, renderer-free. */
export interface Cell {
  x: number;
  y: number;
}

export const cellKey = (x: number, y: number): string => `${String(x)},${String(y)}`;

/** Surface slot address: `${parentInstanceId}#${slotIndex}` (content-schemas §10.1). */
export type SlotKey = `i_${string}#${number}`;
export const slotKey = (parent: `i_${string}`, index: number): SlotKey => `${parent}#${index}`;
export const slotParent = (key: SlotKey): `i_${string}` => key.slice(0, key.indexOf('#')) as `i_${string}`;
export const slotIndex = (key: SlotKey): number => Number(key.slice(key.indexOf('#') + 1));

/** Footprint after rotation: 90/270 swap width and height. */
export function rotatedFootprint(
  def: Pick<ItemDef, 'footprint'>,
  rotation: Rotation,
): { w: number; h: number } {
  const fp = def.footprint ?? { w: 1, h: 1 };
  return rotation === 90 || rotation === 270 ? { w: fp.h, h: fp.w } : { w: fp.w, h: fp.h };
}

export function rectCells(x: number, y: number, w: number, h: number): Cell[] {
  const cells: Cell[] = [];
  for (let dy = 0; dy < h; dy++) for (let dx = 0; dx < w; dx++) cells.push({ x: x + dx, y: y + dy });
  return cells;
}

/** Floor cells in front of a wall item (left wall runs along y at x = 0, right wall along x at y = 0). */
export function wallFootCells(wall: WallSide, offset: number, span: number): Cell[] {
  return Array.from({ length: span }, (_, i) =>
    wall === 'left' ? { x: 0, y: offset + i } : { x: offset + i, y: 0 },
  );
}

/** Allowed rotations for an item: 1 → [0]; 2 → [0, 90] (90 = mirrored sprite); 4 → all four. */
export function allowedRotations(rotations: 1 | 2 | 4): Rotation[] {
  if (rotations === 1) return [0];
  if (rotations === 2) return [0, 90];
  return [0, 90, 180, 270];
}

export function nextRotation(rotations: 1 | 2 | 4, current: Rotation, step: 1 | -1 = 1): Rotation {
  const list = allowedRotations(rotations);
  const i = list.indexOf(current);
  return list[(((i + step) % list.length) + list.length) % list.length] ?? 0;
}

/** Precomputed cell sets for one room including its unlocked expansions. */
export class RoomGeometry {
  readonly floor = new Set<string>();
  readonly zones = new Map<ZoneId, Set<string>>();
  readonly blockedForObjects = new Set<string>();
  /** Wall lengths in cells; a wall only exists along the original bounding box (cut-away front). */
  readonly wallLength: Record<WallSide, number>;
  readonly openings: Record<WallSide, Set<number>>;

  constructor(
    readonly layout: RoomLayout,
    expansions: readonly ExpansionId[] = [],
  ) {
    const addRects = (rects: readonly GridRect[], into: Set<string>) => {
      for (const r of rects) for (const c of rectCells(r.x, r.y, r.w, r.h)) into.add(cellKey(c.x, c.y));
    };
    addRects(layout.floor, this.floor);
    const zoneDefs = [...layout.zones];
    for (const exp of layout.expansions) {
      if (!expansions.includes(exp.id)) continue;
      addRects(exp.addRects, this.floor);
      zoneDefs.push(...(exp.zones ?? []));
    }
    for (const zone of zoneDefs) {
      const set = this.zones.get(zone.id) ?? new Set<string>();
      addRects(zone.rects, set);
      this.zones.set(zone.id, set);
      if (zone.blocksObjects) addRects(zone.rects, this.blockedForObjects);
    }
    this.wallLength = {
      left: layout.walls.left ? layout.size.h : 0,
      right: layout.walls.right ? layout.size.w : 0,
    };
    this.openings = { left: new Set(), right: new Set() };
    for (const o of layout.openings) {
      for (let i = 0; i < o.span; i++) this.openings[o.wall].add(o.offset + i);
    }
  }

  isFloor(c: Cell): boolean {
    return this.floor.has(cellKey(c.x, c.y));
  }

  zonesOf(cells: readonly Cell[]): ZoneId[] {
    const out: ZoneId[] = [];
    for (const [zone, set] of this.zones) {
      if (cells.some((c) => set.has(cellKey(c.x, c.y)))) out.push(zone);
    }
    return out;
  }

  /** A floor cell touching a back wall. Expansion cells outside the original box have no wall. */
  touchesWall(c: Cell): boolean {
    const inBox = c.x >= 0 && c.y >= 0 && c.x < this.layout.size.w && c.y < this.layout.size.h;
    if (!inBox) return false;
    return (c.x === 0 && this.layout.walls.left) || (c.y === 0 && this.layout.walls.right);
  }
}

export interface BBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function bboxOf(cells: readonly Cell[]): BBox {
  const xs = cells.map((c) => c.x);
  const ys = cells.map((c) => c.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, w: Math.max(...xs) - minX + 1, h: Math.max(...ys) - minY + 1 };
}

/** Chebyshev gap between boxes: 0 when touching by edge or corner (content-schemas §14.2). */
export function chebyshevGap(a: BBox, b: BBox): number {
  const gx = Math.max(0, b.x - (a.x + a.w), a.x - (b.x + b.w));
  const gy = Math.max(0, b.y - (a.y + a.h), a.y - (b.y + b.h));
  return Math.max(gx, gy);
}
